import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import {
  KnowledgeBaseDocumentIcon,
  KnowledgeBaseSourceTag,
} from '@/components/knowledge-base/KnowledgeBaseDocumentIcon'
import {
  searchKnowledgeBase,
  splitHighlights,
} from '@/lib/misc/knowledgeBaseContent'
import { cn } from '@/lib/misc/Utils'
import { useKnowledgeBaseIndex } from '@/lib/queries/KnowledgeBase.queries'

import MagnifyingGlassIcon from '@icons/magnifying-glass'
import XMarkIcon from '@icons/x-mark'

const Highlighted = ({ text, terms }: { text: string; terms: string[] }) => (
  <>
    {splitHighlights(text, terms).map((part, index) =>
      part.match ? (
        <mark
          key={index}
          className="rounded-[3px] bg-accent-20 px-px text-accent-60"
        >
          {part.text}
        </mark>
      ) : (
        <span key={index}>{part.text}</span>
      )
    )}
  </>
)

interface KnowledgeBaseSearchProps {
  onClose: () => void
  onSelect: (key: string) => void
}

const KnowledgeBaseSearch = ({
  onClose,
  onSelect,
}: KnowledgeBaseSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const { index, isLoading } = useKnowledgeBaseIndex(true)

  const results = useMemo(
    () => searchKnowledgeBase(index, query),
    [index, query]
  )

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      onClose()
      return
    }
    if (results.length === 0) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const delta = event.key === 'ArrowDown' ? 1 : -1
      setSelected(
        (current) => (current + delta + results.length) % results.length
      )
    } else if (event.key === 'Enter') {
      event.preventDefault()
      onSelect(results[selected].key)
    }
  }

  const showResults = query.trim().length > 0

  return (
    <>
      <label className="pointer-events-auto flex h-9 min-w-0 flex-1 items-center gap-2 rounded-xl border border-hairline/80 bg-surface/80 pr-[3px] pl-2.5 text-accent-100 shadow-raised backdrop-blur-xl backdrop-saturate-150">
        <MagnifyingGlassIcon className="size-4 shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setSelected(0)
          }}
          onKeyDown={onKeyDown}
          placeholder="Search docs…"
          aria-label="Search the knowledge base"
          aria-controls="knowledge-base-results"
          autoComplete="off"
          className="h-full min-w-0 flex-1 bg-transparent text-[14px] text-text-base outline-none placeholder:text-neutral-45"
        />
        <button
          type="button"
          onClick={onClose}
          title="Close search (Esc)"
          aria-label="Close search"
          className="grid size-7.5 place-items-center rounded-lg text-neutral-50 transition-colors hover:bg-neutral-10 hover:text-text-base"
        >
          <XMarkIcon className="size-4" />
        </button>
      </label>

      {showResults && (
        <div
          id="knowledge-base-results"
          role="listbox"
          className="pointer-events-auto absolute top-12 right-0 left-0 max-h-[min(60vh,560px)] overflow-y-auto rounded-2xl border border-hairline/80 bg-surface/95 p-1.5 shadow-popover backdrop-blur-xl"
        >
          {results.map((result, position) => (
            <button
              key={result.key}
              type="button"
              role="option"
              aria-selected={position === selected}
              onMouseEnter={() => setSelected(position)}
              onClick={() => onSelect(result.key)}
              className={cn(
                'block w-full rounded-lg px-2.5 py-2 text-left',
                position === selected && 'bg-accent-05'
              )}
            >
              <span className="flex items-center gap-1.5 text-[13px] font-semibold">
                <KnowledgeBaseDocumentIcon
                  document={result.document}
                  size="sm"
                />
                <span className="truncate">{result.document.title}</span>
                <KnowledgeBaseSourceTag path={result.document.path} />
                {result.heading && (
                  <span className="truncate font-normal text-neutral-45">
                    › <Highlighted text={result.heading} terms={result.terms} />
                  </span>
                )}
              </span>
              <span className="mt-0.5 line-clamp-2 text-[12.5px] leading-[1.45] text-neutral-50">
                <Highlighted text={result.snippet} terms={result.terms} />
              </span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="px-4 py-5 text-center text-[13px] text-neutral-50">
              {isLoading
                ? 'Loading documentation…'
                : `No results for “${query.trim()}”.`}
            </p>
          )}
          {results.length > 0 && (
            <p className="mt-1 flex gap-3.5 border-t border-neutral-10 px-2.5 pt-2 pb-1 text-[11.5px] text-neutral-45">
              <span>↵ open</span>
              <span>↑↓ navigate</span>
              <span>esc close</span>
            </p>
          )}
        </div>
      )}
    </>
  )
}

export default KnowledgeBaseSearch
